import React, { useState } from 'react';

interface GrantPortalAccessModalProps {
  leadId: string;
  leadEmail?: string | null; // Pre-fill from lead record if available
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * PORTAL-08: Internal CRM modal for CEO/Manager/GM to send a portal magic link to a client.
 * Renders inside LeadDetailPage or any internal page — uses internal JWT auth (standard fetch with Authorization).
 * Pre-fills from lead.email but allows override.
 */
export const GrantPortalAccessModal: React.FC<GrantPortalAccessModalProps> = ({
  leadId,
  leadEmail,
  onClose,
  onSuccess,
}) => {
  const [email, setEmail] = useState(leadEmail || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token'); // Internal JWT — standard CRM auth
      const res = await fetch(`/api/leads/${leadId}/portal-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await res.json();

      if (data.success) {
        setSent(true);
        onSuccess();
      } else {
        setError(data.message || 'Failed to send portal access');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Grant Client Portal Access</h2>
        <p className="text-sm text-gray-500 mb-5">
          A secure magic link will be sent to the client's email. Links expire after 24 hours.
        </p>

        {sent ? (
          <div className="text-center py-4">
            <div className="text-green-600 text-lg font-semibold mb-2">Magic link sent!</div>
            <p className="text-sm text-gray-500 mb-4">
              A portal access link has been sent to <strong>{email}</strong>.
            </p>
            <button
              onClick={onClose}
              className="px-5 py-2 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-800"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="client@example.com"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-1">
                Pre-filled from lead record. Override if the client uses a different email.
              </p>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2 bg-blue-700 text-white rounded-lg text-sm font-medium hover:bg-blue-800 disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Magic Link'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
